import { useCallback, useEffect, useState } from "react";
import React from "react";
import { ActivityView } from "./components/ActivityView";
import { AddVisitModal } from "./components/AddVisitModal";
import { ClientDetail } from "./components/ClientDetail";
import { ClientsView } from "./components/ClientsView";
import { Dashboard } from "./components/Dashboard";
import { PropertiesView } from "./components/PropertiesView";
import { PropertyDetail } from "./components/PropertyDetail";
import { QuickCreate } from "./components/QuickCreate";
import { SettingsView } from "./components/SettingsView";
import { VisitsView } from "./components/VisitsView";
import { I_Building, I_Cal, I_Home, I_Layers, I_LogOut, I_Pulse, I_Settings, I_Users } from "./components/icons";
import { Spinner } from "./components/ui";
import { logActivity } from "./lib/activity.js";
import { AuthScreen } from "./screens/AuthScreen";
import { ConfigScreen } from "./screens/ConfigScreen";
import { ErrorScreen } from "./screens/ErrorScreen";
import { ROLE_LABEL } from "./constants.js";
import { heading } from "./styles.js";
import { isConfigured, sb, sbInitError } from "./supabase.js";

const App = () => {
    const [session, setSession] = useState(null);
    const [authReady, setAuthReady] = useState(false);
    const [bootError, setBootError] = useState(null);
    const [view, setView] = useState("dashboard");
    const [clientTab, setClientTab] = useState("all"); // all | allotted | unallotted
    const [clients, setClients] = useState([]);
    const [properties, setProperties] = useState([]);
    const [visits, setVisits] = useState([]);
    const [calendarNotes, setCalendarNotes] = useState([]);
    const [activity, setActivity] = useState([]);
    const [openClient, setOpenClient] = useState(null);
    const [openProperty, setOpenProperty] = useState(null);
    const [quickKind, setQuickKind] = useState(null);
    const [visitFor, setVisitFor] = useState(null); // {propertyId | null}
    const [showVisitModal, setShowVisitModal] = useState(false);

    // auth
    useEffect(() => {
      if (!sb) {
        setAuthReady(true);
        return;
      }
      let settled = false;
      const enrichSession = async (s) => {
        if (!s) return null;
        try {
          const { data: prof } = await sb
            .from("profiles")
            .select("role, can_delete_clients, full_name")
            .eq("id", s.user.id)
            .single();
          return {
            ...s,
            role: prof?.role || "agent",
            canDeleteClients: !!prof?.can_delete_clients,
            fullName: prof?.full_name || s.user.email,
          };
        } catch {
          return {
            ...s,
            role: "agent",
            canDeleteClients: false,
            fullName: s.user.email,
          };
        }
      };
      sb.auth
        .getSession()
        .then(async ({ data }) => {
          settled = true;
          if (data?.session) setSession(await enrichSession(data.session));
          setAuthReady(true);
        })
        .catch((e) => {
          settled = true;
          setBootError("Couldn't reach Supabase. " + (e.message || e));
          setAuthReady(true);
        });
      const wd = setTimeout(() => {
        if (!settled) {
          setBootError(
            "Still trying to reach Supabase after 12 seconds. Check your URL and connection.",
          );
          setAuthReady(true);
        }
      }, 12000);
      const { data: sub } = sb.auth.onAuthStateChange(async (_e, s) =>
        setSession(await enrichSession(s)),
      );
      return () => {
        clearTimeout(wd);
        sub.subscription.unsubscribe();
      };
    }, []);

    // ===== ACCESS TRACKING =====
    // Captures device info + (with explicit user consent) location.
    // Inserts ONE row per page-load into public.access_log for audit purposes.
    // Disclosed on the sign-in screen — see the small note there.
    const accessLoggedRef = React.useRef(false);
    useEffect(() => {
      if (!session || !sb || accessLoggedRef.current) return;
      accessLoggedRef.current = true;
      const log = async () => {
        const access = {
          user_id: session.user.id,
          email: session.user.email || null,
          full_name: session.fullName || null,
          user_agent: (navigator.userAgent || "").slice(0, 500),
          platform: (navigator.platform || "").slice(0, 100),
          screen_size: `${window.screen.width}x${window.screen.height}`,
          language: (navigator.language || "").slice(0, 20),
          timezone: (() => {
            try {
              return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
            } catch {
              return "";
            }
          })(),
          location_granted: false,
        };
        // Try to get location — browser will prompt the user
        if ("geolocation" in navigator) {
          try {
            await new Promise((resolve) => {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  access.location_granted = true;
                  access.latitude = pos.coords.latitude;
                  access.longitude = pos.coords.longitude;
                  access.accuracy_meters = pos.coords.accuracy;
                  resolve();
                },
                () => resolve(),
                {
                  timeout: 12000,
                  maximumAge: 300000,
                  enableHighAccuracy: false,
                },
              );
            });
          } catch (e) {
            /* user denied or geolocation blocked */
          }
        }
        try {
          await sb.from("access_log").insert(access);
        } catch (e) {
          /* silent — don't block the app */
        }
      };
      // Fire-and-forget so it doesn't block first render
      log();
    }, [session?.user?.id]);

    // Refresh role/permission on demand (used by Settings + window focus)
    const refreshProfile = useCallback(async () => {
      if (!sb || !session) return;
      try {
        const { data: prof } = await sb
          .from("profiles")
          .select("role, can_delete_clients, full_name")
          .eq("id", session.user.id)
          .single();
        if (prof)
          setSession((s) =>
            s
              ? {
                  ...s,
                  role: prof.role || "agent",
                  canDeleteClients: !!prof.can_delete_clients,
                  fullName: prof.full_name || s.user.email,
                }
              : s,
          );
      } catch (e) {
        /* keep previous role */
      }
    }, [session?.user?.id]);

    // Auto-refresh profile when tab regains focus (catches role changes made via SQL or another admin)
    useEffect(() => {
      if (!session) return;
      const onFocus = () => refreshProfile();
      window.addEventListener("focus", onFocus);
      return () => window.removeEventListener("focus", onFocus);
    }, [session?.user?.id, refreshProfile]);

    // Refresh profile whenever we navigate to Settings (so role changes show immediately)
    useEffect(() => {
      if (view === "settings") refreshProfile();
    }, [view]);

    // initial loads
    const loadClients = useCallback(async () => {
      // list view doesn't need full photo column on every row, but with 175 clients ~ 1.2MB it's manageable; trade simplicity for one fetch
      try {
        const { data } = await sb.from("clients").select("*").order("updated_at", {
          ascending: false,
        });
        setClients(data || []);
      } catch (e) {}
    }, []);
    const loadProperties = useCallback(async () => {
      try {
        const { data } = await sb.from("properties").select("*").order("updated_at", {
          ascending: false,
        });
        setProperties(data || []);
      } catch (e) {}
    }, []);
    const loadVisits = useCallback(async () => {
      try {
        const { data } = await sb
          .from("visits")
          .select("*")
          .order("visited_at", {
            ascending: false,
          })
          .limit(500);
        setVisits(data || []);
      } catch (e) {}
    }, []);
    const loadCalendarNotes = useCallback(async () => {
      try {
        const { data } = await sb.from("calendar_notes").select("*").order("note_date");
        setCalendarNotes(data || []);
      } catch (e) {}
    }, []);
    const loadActivity = useCallback(async () => {
      try {
        const { data } = await sb
          .from("activity")
          .select("*")
          .order("created_at", {
            ascending: false,
          })
          .limit(50);
        setActivity(data || []);
      } catch (e) {}
    }, []);
    useEffect(() => {
      if (session) {
        loadClients();
        loadProperties();
        loadVisits();
        loadCalendarNotes();
        loadActivity();
      }
    }, [session]);

    // realtime
    useEffect(() => {
      if (!session || !sb) return;
      const ch = sb
        .channel("estatebook-rt")
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "clients",
          },
          () => loadClients(),
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "properties",
          },
          () => loadProperties(),
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "visits",
          },
          () => loadVisits(),
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "activity",
          },
          () => loadActivity(),
        )
        .subscribe();
      return () => {
        sb.removeChannel(ch);
      };
    }, [session]);
    if (!isConfigured) return <ConfigScreen />;
    if (sbInitError) return <ErrorScreen title="Configuration problem" msg={sbInitError} />;
    if (bootError && !session) return <ErrorScreen title="Can't reach Supabase" msg={bootError} />;
    if (!authReady)
      return (
        <div className="min-h-screen flex items-center justify-center text-slate-500">
          <Spinner size={20} />
        </div>
      );
    if (!session) return <AuthScreen />;
    const nav = [
      {
        id: "dashboard",
        label: "Overview",
        icon: I_Home,
      },
      {
        id: "clients",
        label: "Clients",
        icon: I_Users,
      },
      {
        id: "properties",
        label: "Properties",
        icon: I_Building,
      },
      {
        id: "visits",
        label: "Visits",
        icon: I_Cal,
      },
      {
        id: "activity",
        label: "Activity",
        icon: I_Pulse,
      },
      {
        id: "settings",
        label: "Settings",
        icon: I_Settings,
      },
    ];
    return (
      <div className="min-h-screen text-slate-100">
        <div className="fixed inset-0 -z-10 bg-slate-950" />
        <div className="fixed inset-0 -z-10 opacity-[0.4] bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(252,211,77,0.08),transparent),radial-gradient(ellipse_60%_50%_at_80%_50%,rgba(20,184,166,0.06),transparent)]" />
        <div
          className="fixed inset-0 -z-10 opacity-[0.04]"
          style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-60 flex-col border-r border-white/[0.06] bg-slate-950/40 backdrop-blur-xl z-30 p-4">
          <div className="flex items-center gap-2.5 mb-8 px-2">
            <div className="w-9 h-9 rounded-lg bg-amber-200 text-slate-900 flex items-center justify-center shadow-[0_0_20px_-5px_rgba(252,211,77,0.5)]">
              <I_Layers size={18} />
            </div>
            <span className="text-xl text-slate-100" style={heading}>
              Estatebook
            </span>
          </div>
          <nav className="space-y-0.5 flex-1">
            {nav.map((n) => {
              const on = view === n.id && (n.id !== "clients" || clientTab === "all");
              return (
                <React.Fragment key={n.id}>
                  <button
                    onClick={() => {
                      setView(n.id);
                      if (n.id === "clients") setClientTab("all");
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-3 transition ${on ? "bg-white/[0.08] text-slate-100" : "text-slate-400 hover:text-slate-100 hover:bg-white/[0.04]"}`}
                  >
                    <n.icon size={16} />
                    {n.label}
                  </button>
                  {n.id === "clients" &&
                    [
                      { id: "allotted", label: "Allotted", count: clients.filter((c) => !!c.allotted).length },
                      { id: "unallotted", label: "Unallotted", count: clients.filter((c) => !c.allotted).length },
                    ].map((t) => (
                      <button
                        key={t.id}
                        onClick={() => {
                          setView("clients");
                          setClientTab(t.id);
                        }}
                        className={`w-full text-left pl-10 pr-3 py-1.5 rounded-lg text-[13px] flex items-center justify-between transition ${view === "clients" && clientTab === t.id ? "bg-white/[0.08] text-slate-100" : "text-slate-500 hover:text-slate-100 hover:bg-white/[0.04]"}`}
                      >
                        {t.label}
                        <span className="text-[11px] text-slate-500">{t.count}</span>
                      </button>
                    ))}
                </React.Fragment>
              );
            })}
          </nav>
          <div className="border-t border-white/[0.06] pt-3 mt-3">
            <div className="px-3 py-2 text-[11px] text-slate-500 truncate">
              {session.user.email}
            </div>
          </div>
        </aside>
        <main className="lg:ml-60 min-h-screen pb-24 lg:pb-8">
          <div className="lg:hidden sticky top-0 z-20 bg-slate-950/70 backdrop-blur-xl border-b border-white/[0.06] px-4 py-3 flex items-center justify-between">
            <button
              onClick={() => setView("dashboard")}
              className="flex items-center gap-2 min-w-0"
            >
              <div className="w-8 h-8 rounded-lg bg-amber-200 text-slate-900 flex items-center justify-center shrink-0">
                <I_Layers size={16} />
              </div>
              <span className="text-lg" style={heading}>
                Estatebook
              </span>
            </button>
            <div className="flex items-center gap-2">
              <div className="text-right min-w-0">
                <div className="text-[11px] text-slate-300 truncate max-w-[140px]">
                  {session.fullName || session.user.email}
                </div>
                <div
                  className={`text-[10px] uppercase tracking-wider ${session.role === "admin" ? "text-amber-300" : session.role === "power" ? "text-emerald-300" : "text-slate-500"}`}
                >
                  {ROLE_LABEL[session.role] || "Agent"}
                </div>
              </div>
              <button
                onClick={() => sb.auth.signOut()}
                title="Sign out"
                className="p-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-300 hover:text-slate-100 hover:bg-white/[0.08]"
              >
                <I_LogOut size={14} />
              </button>
            </div>
          </div>
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            {view === "dashboard" && (
              <Dashboard
                clients={clients}
                properties={properties}
                visits={visits}
                activity={activity}
                onOpenClient={setOpenClient}
                onOpenProperty={setOpenProperty}
                onNav={setView}
              />
            )}
            {view === "clients" && (
              <ClientsView
                key={clientTab}
                clients={clients}
                allotment={clientTab}
                onAllotment={setClientTab}
                onOpen={setOpenClient}
                onNew={() => setQuickKind("client")}
              />
            )}
            {view === "properties" && (
              <PropertiesView
                properties={properties}
                onOpen={setOpenProperty}
                onNew={() => setQuickKind("property")}
              />
            )}
            {view === "visits" && (
              <VisitsView
                visits={visits}
                clients={clients}
                properties={properties}
                notes={calendarNotes}
                onAddVisit={(pid) => {
                  setVisitFor(pid);
                  setShowVisitModal(true);
                }}
                onAddNote={async (n) => {
                  const { error } = await sb.from("calendar_notes").insert({
                    ...n,
                    created_by: session.user.id,
                  });
                  if (error) throw error;
                  await loadCalendarNotes();
                  await logActivity(
                    session.user.id,
                    "created",
                    "import",
                    null,
                    `Added calendar note: ${n.title} (${n.note_date})`,
                  );
                }}
                onDeleteNote={async (id) => {
                  await sb.from("calendar_notes").delete().eq("id", id);
                  await loadCalendarNotes();
                }}
                onOpenClient={setOpenClient}
                onOpenProperty={setOpenProperty}
                onChanged={() => {
                  loadVisits();
                  loadActivity();
                }}
                session={session}
              />
            )}
            {view === "activity" && (
              <ActivityView
                activity={activity}
                clients={clients}
                properties={properties}
                onOpenClient={setOpenClient}
                onOpenProperty={setOpenProperty}
              />
            )}
            {view === "settings" && (
              <SettingsView
                session={session}
                clients={clients}
                properties={properties}
                visits={visits}
                onChanged={() => {
                  loadClients();
                  loadProperties();
                  loadActivity();
                }}
                onSignOut={() => sb.auth.signOut()}
                onRefreshProfile={refreshProfile}
              />
            )}
          </div>
        </main>
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-slate-950/85 backdrop-blur-xl border-t border-white/[0.06] flex items-stretch justify-around safe-bottom">
          {nav.map((n) => (
            <button
              key={n.id}
              onClick={() => setView(n.id)}
              className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 ${view === n.id ? "text-amber-200" : "text-slate-500 hover:text-slate-300"}`}
            >
              <n.icon size={17} />
              <span className="text-[9px]">{n.label}</span>
            </button>
          ))}
        </nav>
        {openClient && (
          <ClientDetail
            clientId={openClient}
            session={session}
            properties={properties}
            visits={visits}
            onClose={() => setOpenClient(null)}
            onChanged={() => {
              loadClients();
              loadActivity();
            }}
            onOpenProperty={(pid) => {
              setOpenClient(null);
              setTimeout(() => setOpenProperty(pid), 50);
            }}
          />
        )}
        {openProperty && (
          <PropertyDetail
            propertyId={openProperty}
            session={session}
            clients={clients}
            visits={visits}
            onClose={() => setOpenProperty(null)}
            onChanged={() => {
              loadProperties();
              loadActivity();
            }}
            onOpenClient={(cid) => {
              setOpenProperty(null);
              setTimeout(() => setOpenClient(cid), 50);
            }}
            onAddVisit={(pid) => {
              setVisitFor(pid);
              setShowVisitModal(true);
            }}
          />
        )}
        {quickKind && (
          <QuickCreate
            kind={quickKind}
            session={session}
            onClose={() => setQuickKind(null)}
            onCreated={(id) => {
              if (quickKind === "client") {
                loadClients();
                setOpenClient(id);
              } else {
                loadProperties();
                setOpenProperty(id);
              }
              loadActivity();
            }}
          />
        )}
        {showVisitModal && (
          <AddVisitModal
            initialPropertyId={visitFor}
            clients={clients}
            properties={properties}
            session={session}
            onClose={() => {
              setShowVisitModal(false);
              setVisitFor(null);
            }}
            onSaved={() => {
              loadVisits();
              loadActivity();
            }}
          />
        )}
      </div>
    );
  };

export { App };
